import { useState, useCallback, useTransition, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowRight, CircleAlert } from 'lucide-react';

import { AuthSplitLayout } from '@/components/layout/AuthSplitLayout';
import { Field } from '@/components/common/Field';
import { Button, Alert, Input, Label } from '@agion/ui';
import { config } from '@/utils/config';
import { newPasswordHint } from '@/services/password-hint';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState<string>('');
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // Use useCallback to memoize handlers and prevent recreation on every render
  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Update form data - using functional update to avoid stale closures
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear field-specific error
    setErrors(prev => {
      if (!prev[name as keyof FormData]) return prev;
      const { [name as keyof FormData]: _, ...rest } = prev;
      return rest;
    });

    // Clear global error
    if (error) setError('');
  }, [error]);

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    // The server judges the password and answers with its own sentence;
    // the field only states the rule (agion/Agion#3140, B4).
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the Terms of Service';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setError('');

    // Use transition for async operation
    startTransition(async () => {
      try {
        // Use auth-service API endpoint via APISIX gateway
        // Organization will be created during admin-ui onboarding
        const apiUrl = `${config.apiUrl}/auth/register`;

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          credentials: 'include',
          mode: 'cors',
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            first_name: formData.firstName,
            last_name: formData.lastName,
          }),
        });

        const contentType = response.headers.get('content-type');

        if (!response.ok) {
          let errorMessage = 'Registration failed';

          // Handle specific status codes
          if (response.status === 409) {
            errorMessage = 'An account with this email already exists. Please use the login page instead.';
          } else if (contentType?.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } else {
            const textError = await response.text();
            console.error('Non-JSON error response:', textError);
            errorMessage = `Registration failed (${response.status})`;
          }

          throw new Error(errorMessage);
        }

        if (!contentType?.includes('application/json')) {
          throw new Error('Invalid response from server');
        }

        const result = await response.json();

        if (result.success) {
          // P-017: Route based on verification_status from backend
          // verification_status determines which page the user should see:
          // - 'pending_approval': domain is verified, user needs admin approval
          // - 'email_verification_pending': new domain, user needs to verify email
          const verificationStatus = result.verification_status || 'email_verification_pending';

          if (verificationStatus === 'pending_approval') {
            // P-017: Domain is already verified by an organization
            // User is added as pending member, awaiting admin approval
            const orgName = result.organization_name || '';
            const pendingUrl = orgName
              ? `/pending-approval?org=${encodeURIComponent(orgName)}`
              : '/pending-approval';
            navigate(pendingUrl);
          } else if (verificationStatus === 'email_verification_pending') {
            // Standard flow: new domain, user needs to verify email
            const verifyUrl = `/verify-email-pending?email=${encodeURIComponent(formData.email)}`;
            navigate(verifyUrl);
          } else {
            // Unknown status - log warning and default to email verification
            console.warn('[Registration] Unknown verification_status received:', verificationStatus);
            const verifyUrl = `/verify-email-pending?email=${encodeURIComponent(formData.email)}`;
            navigate(verifyUrl);
          }
        } else {
          throw new Error(result.message || result.error || 'Registration failed');
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Registration failed. Please try again.';
        setError(errorMessage);
        console.error('Registration error:', err);
      }
    });
  }, [formData, navigate, validateForm]);

  const accountExists = error.includes('already exists');

  return (
    <AuthSplitLayout>
      <div className="lp-heading-block lp-rise lp-rise-2">
        <h2 className="lp-heading">{t('register.heading')}</h2>
        <p className="lp-subheading">{t('register.subheading')}</p>
      </div>

      <form onSubmit={handleSubmit} className="lp-form lp-rise lp-rise-3">
        {error && (
          <Alert variant="error">
            <div>
              {error}
              {accountExists && (
                <div>
                  <a href="/login" className="lp-back">
                    {t('register.goToLogin')}
                    <ArrowRight size={16} aria-hidden="true" />
                  </a>
                </div>
              )}
            </div>
          </Alert>
        )}

        <div className="lp-field-row">
          <Field
            id="firstName"
            name="firstName"
            type="text"
            label="First Name"
            value={formData.firstName}
            onChange={handleInputChange}
            error={errors.firstName}
            disabled={isPending}
            placeholder={t('register.firstNamePlaceholder')}
            autoComplete="given-name"
          />

          <Field
            id="lastName"
            name="lastName"
            type="text"
            label="Last Name"
            value={formData.lastName}
            onChange={handleInputChange}
            error={errors.lastName}
            disabled={isPending}
            placeholder={t('register.lastNamePlaceholder')}
            autoComplete="family-name"
          />
        </div>

        <Field
          id="email"
          name="email"
          type="email"
          label="Email Address"
          value={formData.email}
          onChange={handleInputChange}
          error={errors.email}
          disabled={isPending}
          placeholder={t('register.emailPlaceholder')}
          autoComplete="email"
          leadingIcon={<Mail size={16} />}
        />

        <Field
          id="password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          label="Password"
          value={formData.password}
          onChange={handleInputChange}
          error={errors.password}
          disabled={isPending}
          placeholder={t('register.passwordPlaceholder')}
          hint={newPasswordHint(t)}
          autoComplete="new-password"
          labelAction={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="lp-field-action"
              aria-controls="password confirmPassword"
              aria-pressed={showPassword}
              onClick={() => setShowPassword((shown) => !shown)}
            >
              {showPassword ? t('login.hidePassword') : t('login.showPassword')}
            </Button>
          }
        />

        <Field
          id="confirmPassword"
          name="confirmPassword"
          type={showPassword ? 'text' : 'password'}
          label="Confirm Password"
          value={formData.confirmPassword}
          onChange={handleInputChange}
          error={errors.confirmPassword}
          disabled={isPending}
          placeholder={t('register.confirmPasswordPlaceholder')}
          autoComplete="new-password"
        />

        <div>
          <div className="lp-check">
            <Input
              variant="inline"
              id="acceptTerms"
              name="acceptTerms"
              type="checkbox"
              checked={formData.acceptTerms}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, acceptTerms: (e.target as HTMLInputElement).checked }));
                if (errors.acceptTerms) {
                  setErrors(prev => {
                    const { acceptTerms: _, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              disabled={isPending}
              className="lp-check-input"
            />
            <Label htmlFor="acceptTerms" className="lp-check-label">
              {t('register.acceptTermsLead')}{' '}
              <a href="/terms" target="_blank">
                {t('register.termsOfService')}
              </a>{' '}
              {t('register.acceptTermsJoin')}{' '}
              <a href="/privacy" target="_blank">
                {t('register.privacyPolicy')}
              </a>
            </Label>
          </div>
          {errors.acceptTerms && (
            <p className="lp-field-msg lp-field-msg-error">
              <CircleAlert size={14} strokeWidth={2.5} aria-hidden="true" />
              {errors.acceptTerms}
            </p>
          )}
        </div>

        <Button
          type="submit"
          className="lp-btn-primary w-full"
          disabled={isPending}
          isLoading={isPending}
        >
          {isPending ? t('register.creatingButton') : t('register.submitButton')}
          {!isPending && <ArrowRight size={16} aria-hidden="true" />}
        </Button>
      </form>

      <p className="lp-secondary-cta lp-rise lp-rise-4">
        {t('register.alreadyHaveAccount')}{' '}
        <Link to="/login">{t('register.signIn')}</Link>
      </p>
    </AuthSplitLayout>
  );
}
